/* Sully & Co. Coastal Soft Stretch promo page (templates/page.sully-bogo.json).
   Loaded by every sully-bogo-* section; the guard below keeps it to one run.

   Hero and picker (sections/sully-bogo-builder.liquid):
     - One picker, two polos. The Your First Polo / Your Second Polo tabs choose which polo the
       swatches, flat lays and sizes edit. The hero photo shows the colorway just chosen.
     - No size is ever preselected. Picking the first polo's size moves on to the second polo
       when that one has no color yet (section setting).
     - Stock: the colorway JSON the section prints (Liquid, on load), re-read from
       /products/<handle>.js once the page is idle and again after a failed add.
       Low-stock counts ("2 left") come from the page render.
     - Price line: the higher price first, the other at (100 - discount)%. The real discount is
       the store's automatic Buy X Get Y; this page only does the math.
     - Add Both to Cart posts both variants in ONE request to /cart/add.js, then asks the theme
       to refresh its cart (theme:cart:refresh). The theme's Cart rebuilds the drawer and fires
       theme:product:added, which opens the drawer; it announces that with theme:cart-drawer:open.
       If that does not happen in 4s, or the store uses a cart page, we go to /cart instead.
   Phone bar: "Choose Your Two" until both polos are chosen, then "Add Both to Cart, $126".
     It stays out of the way while the offer card or the picker is on screen.
   Jump links ([data-sully-bogo-jump]): scroll to the target under the sticky header and correct
   the position once if anything above it changed height on the way.
   Hero photos: the on-model photo, then the colorway's pop-up photos (the on-model photo skipped
     if it's one of them), in a swipe track with arrows. Stepping through never changes the
     colorway; choosing a colorway starts its photos from the on-model one. Only the photo on
     screen and its neighbours load, and nothing past the first photo before the page has loaded.
   Photo pop-up: a <dialog> with the colorway's product-page photos (JSON "photos"), opened from
     the hero button (the colorway in the photo) or the Color row (the open polo's color). Swipe,
     arrows or the arrow keys move through them; the swatches switch colorway inside the pop-up
     only. Images are created when the pop-up opens, so the page never loads them up front.
   Size chart: the product page's chart, cloned from <template data-size-chart> into the same
     dialog markup the product page uses (assets/sully-size-chart.css).
   Reviews (sections/sully-bogo-reviews.liquid): phones and tablets swipe through every card.
     From 990px "Read all" opens the rest in the grid (CSS, .is-expanded); "Show fewer" puts it
     back and returns to the top of the reviews.
   Video (sections/sully-bogo-feature.liquid): sources load when the video nears the screen;
   nothing autoplays under prefers-reduced-motion. */
(function () {
  'use strict';

  if (window.SullyBogo) return;
  window.SullyBogo = { builders: [] };

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || root + 'cart';

  // "$84", not "$84.00": whole amounts drop their decimals, like money_without_trailing_zeros
  function formatMoney(cents, format) {
    var fmt = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      var decimals = cents % 100 === 0 ? 0 : 2;
      var thousands = ',';
      var point = '.';
      if (key.indexOf('no_decimals') !== -1) decimals = 0;
      if (key.indexOf('comma_separator') !== -1) {
        thousands = '.';
        point = ',';
      }
      if (key.indexOf('apostrophe') !== -1) thousands = "'";
      if (key.indexOf('space_separator') !== -1) {
        thousands = ' ';
        point = ',';
      }
      var parts = (cents / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(point);
    });
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  // "Name.jpg" from a Shopify image URL, to tell whether two URLs are the same photo
  function fileName(src) {
    return String(src || '').split('?')[0].split('/').pop();
  }

  function all(scope, selector) {
    return Array.prototype.slice.call(scope.querySelectorAll(selector));
  }

  function scrollToEl(el) {
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  /* ------------------------------------------------------------------------
     Jump links: land exactly on the target, below the sticky header
     ------------------------------------------------------------------------ */
  function jumpTo(target) {
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    scrollToEl(target);
    var settle = function () {
      var margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      if (Math.abs(target.getBoundingClientRect().top - margin) > 4) {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
      target.focus({ preventScroll: true });
    };
    setTimeout(settle, reduceMotion ? 50 : 900);
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[data-sully-bogo-jump], a[data-sully-bogo-cta]');
    if (!link) return;
    var hash = link.getAttribute('href') || '';
    if (hash.charAt(0) !== '#') return;
    var target = document.getElementById(hash.slice(1));
    if (!target) return;
    event.preventDefault();
    if (history.replaceState) history.replaceState(null, '', hash);
    jumpTo(target);
  });

  /* ------------------------------------------------------------------------
     Hero and picker
     ------------------------------------------------------------------------ */
  function Builder(el) {
    this.el = el;
    this.ds = el.dataset;
    this.pct = Math.min(100, Math.max(0, parseInt(this.ds.discount, 10) || 0));
    this.lowLimit = parseInt(this.ds.lowLimit, 10) || 0;
    this.titles = (this.ds.slotTitles || '').split('|');
    this.colorways = {};
    this.order = [];
    var json = el.querySelector('[data-colorways]');
    var list = [];
    try {
      list = JSON.parse(json ? json.textContent : '[]');
    } catch (e) {
      list = [];
    }
    list.forEach(function (c) {
      this.colorways[c.key] = c;
      this.order.push(c.key);
    }, this);

    var start = el.querySelector('.sully-bogo-flat[aria-pressed="true"]');
    var startKey = start && this.colorways[start.getAttribute('data-colorway')] ? start.getAttribute('data-colorway') : null;
    this.slot = 0;
    this.picks = [{ c: startKey, s: null }, { c: null, s: null }];
    this.shown = startKey || this.order[0] || null;
    this.busy = false;

    this.heroImg = el.querySelector('.sully-bogo-top__img');
    this.heroTrack = el.querySelector('[data-hero-track]');
    this.heroNav = el.querySelector('[data-hero-nav]');
    this.heroPrev = el.querySelector('[data-hero-prev]');
    this.heroNext = el.querySelector('[data-hero-next]');
    this.slotBtns = all(el, '[data-slot]');
    this.colorBtns = all(el, '.sully-bogo-swatch[data-colorway], .sully-bogo-flat[data-colorway]');
    this.colorValues = all(el, '[data-color-value]');
    this.sizeBtns = all(el, '[data-size]');
    this.sizeValue = el.querySelector('[data-size-value]');
    this.same = el.querySelector('[data-same-size]');
    this.sameValue = el.querySelector('[data-same-size-value]');
    this.fitLink = el.querySelector('[data-fit-link]');
    this.priceNodes = all(el, '[data-price]');
    this.add = el.querySelector('[data-add]');
    this.hint = el.querySelector('[data-hint]');
    this.message = el.querySelector('[data-message]');
    this.summary = el.querySelector('[data-summary]');
    this.picker = el.querySelector('[data-picker]');
    this.photosLabel = el.querySelector('[data-photos-label]');
    this.gallery = el.querySelector('[data-gallery]');
    this.sizeChartSource = el.querySelector('[data-size-chart]');
    this.bar = el.querySelector('[data-bar]');
    this.barButton = el.querySelector('[data-bar-button]');
    this.barLabel = el.querySelector('[data-bar-label]');

    if (!this.add || !this.picker) return;
    this.bind();
    this.render();
    this.initBar();

    var self = this;
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1500); };
    var refresh = function () {
      idle(function () {
        self.refreshStock(self.order);
        self.heroReady = true;
        self.heroLoad(self.heroIndex());
      });
    };
    if (document.readyState === 'complete') refresh();
    else window.addEventListener('load', refresh, { once: true });
  }

  Builder.prototype.bind = function () {
    var self = this;
    this.slotBtns.forEach(function (button) {
      button.addEventListener('click', function () {
        self.setSlot(parseInt(button.getAttribute('data-slot'), 10) || 0);
      });
    });
    this.colorBtns.forEach(function (button) {
      button.addEventListener('click', function () {
        self.setColor(button.getAttribute('data-colorway'));
      });
    });
    this.sizeBtns.forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.disabled) return;
        self.setSize(button.getAttribute('data-size'));
      });
    });
    if (this.same) {
      this.same.addEventListener('click', function () {
        var size = self.picks[0].s;
        if (size && self.isAvailable(self.picks[1].c, size)) self.setSize(size);
      });
    }
    this.add.addEventListener('click', function () { self.addToCart(); });

    all(this.el, '[data-gallery-open]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.hasAttribute('data-gallery-from-picker')) {
          self.openGallery(self.picks[self.slot].c || self.shown, 0);
          return;
        }
        // From the hero: open on the photo it's showing, when that photo is in the pop-up
        var slide = self.heroTrack && self.heroTrack.children[self.heroIndex()];
        var at = slide ? parseInt(slide.getAttribute('data-photo'), 10) : 0;
        self.openGallery(self.shown, at > 0 ? at : 0);
      });
    });
    all(this.el, '[data-size-chart-open]').forEach(function (button) {
      button.addEventListener('click', function () { self.openSizeChart(); });
    });
    if (this.gallery) this.bindGallery();
    if (this.heroTrack && this.heroNav) this.bindHero();
  };

  Builder.prototype.variant = function (pick, size) {
    var c = pick.c && this.colorways[pick.c];
    return (c && c.sizes[size || pick.s]) || null;
  };

  // With no colorway yet, a size counts if any colorway has it
  Builder.prototype.isAvailable = function (key, size) {
    if (key) {
      var c = this.colorways[key];
      var v = c && c.sizes[size];
      return !!(v && v.available);
    }
    return this.order.some(function (k) {
      var v = this.colorways[k].sizes[size];
      return !!(v && v.available);
    }, this);
  };

  Builder.prototype.setSlot = function (i) {
    this.slot = i;
    if (this.picks[i].c) this.shown = this.picks[i].c;
    this.clearMessage();
    this.render();
  };

  Builder.prototype.setColor = function (key) {
    if (!this.colorways[key]) return;
    var pick = this.picks[this.slot];
    pick.c = key;
    if (pick.s && !this.isAvailable(key, pick.s)) pick.s = null;
    this.shown = key;
    this.clearMessage();
    this.render();
  };

  Builder.prototype.setSize = function (size) {
    var pick = this.picks[this.slot];
    if (!this.isAvailable(pick.c, size)) return;
    pick.s = size;
    this.clearMessage();
    this.render();
    var self = this;
    if (this.slot === 0 && this.ds.autoAdvance === 'true' && pick.c && !this.picks[1].c) {
      setTimeout(function () {
        if (self.slot === 0 && !self.picks[1].c) self.setSlot(1);
      }, reduceMotion ? 0 : 450);
    }
  };

  Builder.prototype.isComplete = function () {
    return this.picks.every(function (pick) {
      var v = this.variant(pick);
      return !!(v && v.available);
    }, this);
  };

  Builder.prototype.missing = function () {
    for (var i = 0; i < this.picks.length; i++) {
      var v = this.variant(this.picks[i]);
      if (!this.picks[i].c || !(v && v.available)) return i;
    }
    return -1;
  };

  Builder.prototype.maxPrice = function (key) {
    var c = key && this.colorways[key];
    if (!c) return null;
    var max = 0;
    Object.keys(c.sizes).forEach(function (s) { max = Math.max(max, c.sizes[s].price); });
    return max;
  };

  Builder.prototype.prices = function () {
    var self = this;
    var fallback = this.maxPrice(this.order[0]) || 0;
    var each = this.picks.map(function (pick) {
      var v = self.variant(pick);
      if (v) return v.price;
      return self.maxPrice(pick.c);
    });
    if (each[0] === null) each[0] = each[1] !== null ? each[1] : fallback;
    if (each[1] === null) each[1] = each[0];
    var high = Math.max(each[0], each[1]);
    var low = Math.min(each[0], each[1]);
    var second = Math.round(low * (100 - this.pct) / 100);
    var fmt = this.ds.moneyFormat;
    return { first: formatMoney(high, fmt), second: formatMoney(second, fmt), total: formatMoney(high + second, fmt) };
  };

  Builder.prototype.sizeLabel = function (size) {
    var button = this.sizeBtns.filter(function (b) { return b.getAttribute('data-size') === size; })[0];
    var name = button && button.querySelector('.sully-bogo__size-name');
    return name ? name.textContent.trim() : size || '';
  };

  Builder.prototype.renderSlotThumb = function (button, pick) {
    var thumb = button.querySelector('[data-slot-thumb]');
    if (!thumb) return;
    var c = pick.c && this.colorways[pick.c];
    var current = thumb.getAttribute('data-key') || '';
    if (current === (pick.c || '')) return;
    thumb.setAttribute('data-key', pick.c || '');
    thumb.textContent = '';
    if (c && c.flat) {
      var img = document.createElement('img');
      img.src = c.flat;
      img.alt = '';
      img.width = 60;
      img.height = 60;
      thumb.appendChild(img);
    } else {
      var plus = document.createElement('span');
      plus.className = 'sully-bogo-slot__plus';
      plus.textContent = '+';
      thumb.appendChild(plus);
    }
  };

  Builder.prototype.renderHero = function () {
    var c = this.shown && this.colorways[this.shown];
    if (!this.heroImg || !c || !c.hero) return;
    if (this.heroImg.getAttribute('data-key') === this.shown) return;
    // The page renders the first colorway's photo, so the first pass only adds the rest
    var rendered = !this.heroImg.hasAttribute('data-key') && this.shown === this.order[0];
    this.heroImg.setAttribute('data-key', this.shown);
    if (!rendered) {
      if (c.hero.srcset) this.heroImg.setAttribute('srcset', c.hero.srcset);
      else this.heroImg.removeAttribute('srcset');
      this.heroImg.setAttribute('src', c.hero.src);
      this.heroImg.setAttribute('alt', c.hero.alt || '');
    }
    this.renderHeroSlides(c);
  };

  /* ------------------------------------------------------------------------
     Hero photos: swipe or arrows through the shown colorway's photos
     ------------------------------------------------------------------------ */
  Builder.prototype.bindHero = function () {
    var self = this;
    var track = this.heroTrack;
    this.heroPrev.addEventListener('click', function () { self.heroGo(self.heroIndex() - 1); });
    this.heroNext.addEventListener('click', function () { self.heroGo(self.heroIndex() + 1); });
    // A touch on the photo starts loading its neighbours before the swipe moves
    track.addEventListener('pointerdown', function () {
      self.heroReady = true;
      self.heroLoad(self.heroIndex());
    }, { passive: true });
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        self.heroReady = true;
        self.heroLoad(self.heroIndex());
        self.renderHeroNav();
      });
    }, { passive: true });
  };

  Builder.prototype.renderHeroSlides = function (c) {
    var track = this.heroTrack;
    var first = this.heroImg.parentNode;
    if (!track || !this.heroNav || first.parentNode !== track) return;
    while (first.nextSibling) track.removeChild(first.nextSibling);
    first.removeAttribute('data-photo');
    var heroName = fileName(c.hero.src);
    var sizes = this.heroImg.getAttribute('sizes') || '100vw';
    (c.photos || []).forEach(function (photo, i) {
      if (fileName(photo.src) === heroName) {
        first.setAttribute('data-photo', i);
        return;
      }
      var slide = document.createElement('div');
      slide.className = 'sully-bogo-top__slide';
      slide.setAttribute('data-photo', i);
      var img = document.createElement('img');
      img.alt = photo.alt || '';
      img.decoding = 'async';
      img.sizes = sizes;
      img.setAttribute('data-src', photo.src);
      if (photo.srcset) img.setAttribute('data-srcset', photo.srcset);
      slide.appendChild(img);
      track.appendChild(slide);
    });
    track.scrollLeft = 0;
    this.heroNav.hidden = track.children.length < 2;
    this.renderHeroNav();
    if (this.heroReady) this.heroLoad(0);
  };

  // Load the photo at i and the ones either side of it
  Builder.prototype.heroLoad = function (i) {
    if (!this.heroTrack) return;
    var slides = this.heroTrack.children;
    for (var j = Math.max(0, i - 1); j <= Math.min(slides.length - 1, i + 1); j++) {
      var img = slides[j].querySelector('img[data-src]');
      if (!img) continue;
      if (img.getAttribute('data-srcset')) img.setAttribute('srcset', img.getAttribute('data-srcset'));
      img.setAttribute('src', img.getAttribute('data-src'));
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }
  };

  Builder.prototype.heroIndex = function () {
    var track = this.heroTrack;
    if (!track || !track.clientWidth) return 0;
    return Math.min(track.children.length - 1, Math.max(0, Math.round(track.scrollLeft / track.clientWidth)));
  };

  Builder.prototype.heroGo = function (i) {
    var track = this.heroTrack;
    var next = Math.max(0, Math.min(track.children.length - 1, i));
    this.heroReady = true;
    this.heroLoad(next);
    track.scrollTo({ left: next * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  Builder.prototype.renderHeroNav = function () {
    if (!this.heroNav || this.heroNav.hidden) return;
    var i = this.heroIndex();
    this.heroPrev.disabled = i <= 0;
    this.heroNext.disabled = i >= this.heroTrack.children.length - 1;
  };

  Builder.prototype.render = function () {
    var self = this;
    var pick = this.picks[this.slot];
    var color = pick.c && this.colorways[pick.c];

    this.slotBtns.forEach(function (button) {
      var i = parseInt(button.getAttribute('data-slot'), 10) || 0;
      var p = self.picks[i];
      var c = p.c && self.colorways[p.c];
      button.setAttribute('aria-pressed', String(i === self.slot));
      var sub = button.querySelector('[data-slot-sub]');
      if (sub) sub.textContent = c ? c.name + ' · ' + (p.s ? self.sizeLabel(p.s) : self.ds.pickSize) : self.ds.pickColor;
      self.renderSlotThumb(button, p);
    });

    this.colorBtns.forEach(function (button) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-colorway') === pick.c));
    });
    this.colorValues.forEach(function (node) { node.textContent = color ? color.name : ''; });

    this.sizeBtns.forEach(function (button) {
      var size = button.getAttribute('data-size');
      var available = self.isAvailable(pick.c, size);
      button.disabled = !available;
      button.setAttribute('aria-pressed', String(size === pick.s));
      var note = button.querySelector('[data-size-note]');
      if (!note) return;
      var v = color && color.sizes[size];
      var low = v && v.available && self.lowLimit > 0 && v.qty > 0 && v.qty <= self.lowLimit;
      note.textContent = low ? fill(self.ds.lowStock, { count: v.qty }) : '';
    });
    if (this.sizeValue) this.sizeValue.textContent = pick.s ? this.sizeLabel(pick.s) : '';

    var firstSize = this.picks[0].s;
    var showSame = this.slot === 1 && !!firstSize;
    if (this.same) {
      this.same.hidden = !showSame;
      if (showSame) {
        this.same.disabled = !this.isAvailable(pick.c, firstSize);
        this.same.setAttribute('aria-pressed', String(pick.s === firstSize));
        // A no-break space: the button is a flex box, which drops a plain leading space
        if (this.sameValue) this.sameValue.textContent = '\u00a0· ' + this.sizeLabel(firstSize);
      }
    }
    if (this.fitLink) this.fitLink.hidden = showSame;

    this.renderHero();
    this.renderPhotosLabel();

    var p = this.prices();
    var complete = this.isComplete();
    var line = fill(this.ds.priceTemplate, p);
    this.priceNodes.forEach(function (node) { node.textContent = line; });
    this.add.disabled = !complete || this.busy;
    this.add.classList.toggle('is-busy', this.busy);
    this.add.setAttribute('aria-busy', String(this.busy));

    if (this.hint) {
      var m = this.missing();
      if (m === -1) this.hint.textContent = '';
      else this.hint.textContent = fill(this.picks[m].c ? this.ds.hintSize : this.ds.hintColor, { polo: this.titles[m] || '' });
    }

    if (this.barButton) {
      this.barLabel.textContent = complete ? fill(this.ds.barAdd, p) : this.ds.barChoose;
      this.barButton.disabled = this.busy;
      this.barButton.classList.toggle('is-busy', this.busy);
    }
    this.updateBar();
  };

  Builder.prototype.showMessage = function (text) {
    if (this.message) this.message.textContent = text || '';
  };

  Builder.prototype.clearMessage = function () {
    this.showMessage('');
  };

  Builder.prototype.renderPhotosLabel = function () {
    var c = this.shown && this.colorways[this.shown];
    if (!this.photosLabel || !c || !c.photos) return;
    this.photosLabel.textContent = fill(this.photosLabel.getAttribute('data-template'), { count: c.photos.length });
  };

  /* ------------------------------------------------------------------------
     Photo pop-up
     ------------------------------------------------------------------------ */
  Builder.prototype.bindGallery = function () {
    var self = this;
    var g = this.gallery;
    this.galleryTrack = g.querySelector('[data-gallery-track]');
    this.galleryTitle = g.querySelector('[data-gallery-title]');
    this.galleryCount = g.querySelector('[data-gallery-count]');
    this.galleryPrev = g.querySelector('[data-gallery-prev]');
    this.galleryNext = g.querySelector('[data-gallery-next]');
    this.gallerySwatches = all(g, '[data-gallery-colorway]');

    g.querySelector('[data-gallery-close]').addEventListener('click', function () { g.close(); });
    // A click on the dimmed backdrop (outside the panel) closes it
    g.addEventListener('click', function (event) {
      if (event.target === g) g.close();
    });
    g.addEventListener('close', function () {
      document.documentElement.classList.remove('sully-bogo-lock');
    });
    g.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') { event.preventDefault(); self.galleryGo(self.galleryIndex() + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); self.galleryGo(self.galleryIndex() - 1); }
    });
    this.galleryPrev.addEventListener('click', function () { self.galleryGo(self.galleryIndex() - 1); });
    this.galleryNext.addEventListener('click', function () { self.galleryGo(self.galleryIndex() + 1); });
    this.gallerySwatches.forEach(function (button) {
      button.addEventListener('click', function () { self.renderGallery(button.getAttribute('data-gallery-colorway')); });
    });
    var ticking = false;
    this.galleryTrack.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        self.renderGalleryCount();
      });
    }, { passive: true });
  };

  Builder.prototype.openGallery = function (key, at) {
    var g = this.gallery;
    if (!g || !this.colorways[key]) return;
    this.renderGallery(key);
    if (typeof g.showModal === 'function') g.showModal();
    else g.setAttribute('open', '');
    document.documentElement.classList.add('sully-bogo-lock');
    this.galleryTrack.scrollLeft = (at || 0) * this.galleryTrack.clientWidth;
    this.renderGalleryCount();
  };

  Builder.prototype.renderGallery = function (key) {
    var c = this.colorways[key];
    if (!c) return;
    this.galleryKey = key;
    this.galleryTitle.textContent = c.name;
    this.gallerySwatches.forEach(function (button) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-gallery-colorway') === key));
    });
    var track = this.galleryTrack;
    track.textContent = '';
    (c.photos || []).forEach(function (photo, i) {
      var li = document.createElement('li');
      li.className = 'sully-bogo-gallery__slide';
      var img = document.createElement('img');
      img.src = photo.src;
      if (photo.srcset) img.srcset = photo.srcset;
      img.sizes = '(min-width: 990px) 760px, 100vw';
      img.alt = photo.alt || '';
      img.width = photo.w;
      img.height = photo.h;
      img.decoding = 'async';
      if (i > 1) img.loading = 'lazy';
      li.appendChild(img);
      track.appendChild(li);
    });
    track.scrollLeft = 0;
    this.renderGalleryCount();
  };

  Builder.prototype.galleryIndex = function () {
    var track = this.galleryTrack;
    return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
  };

  Builder.prototype.galleryGo = function (i) {
    var track = this.galleryTrack;
    var total = track.children.length;
    if (!total) return;
    var next = Math.max(0, Math.min(total - 1, i));
    track.scrollTo({ left: next * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  Builder.prototype.renderGalleryCount = function () {
    var total = this.galleryTrack.children.length;
    var i = Math.min(this.galleryIndex(), Math.max(0, total - 1));
    this.galleryCount.textContent = total ? i + 1 + ' / ' + total : '';
    this.galleryPrev.disabled = i <= 0;
    this.galleryNext.disabled = i >= total - 1;
  };

  /* ------------------------------------------------------------------------
     Size chart: the product page's chart and dialog markup
     ------------------------------------------------------------------------ */
  Builder.prototype.openSizeChart = function () {
    var source = this.sizeChartSource;
    if (!source || !source.content) return;
    if (!this.sizeChart) {
      var dialog = document.createElement('dialog');
      dialog.className = 'sully-sc';
      dialog.setAttribute('aria-labelledby', 'SullyBogoSizeChartTitle');
      var head = document.createElement('div');
      head.className = 'sully-sc__head';
      var title = document.createElement('h2');
      title.className = 'sully-sc__title';
      title.id = 'SullyBogoSizeChartTitle';
      title.textContent = source.getAttribute('data-title') || 'Size Chart';
      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'sully-sc__close';
      close.setAttribute('aria-label', 'Close size chart');
      close.innerHTML = '<svg viewBox="0 0 18 18" aria-hidden="true" focusable="false"><path d="M2 2l14 14M16 2L2 16" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';
      close.addEventListener('click', function () { dialog.close(); });
      head.appendChild(title);
      head.appendChild(close);
      var body = document.createElement('div');
      body.className = 'sully-sc__body';
      body.appendChild(source.content.cloneNode(true));
      dialog.appendChild(head);
      dialog.appendChild(body);
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', function () {
        document.documentElement.classList.remove('sully-sc-lock');
      });
      document.body.appendChild(dialog);
      this.sizeChart = dialog;
    }
    if (typeof this.sizeChart.showModal === 'function') this.sizeChart.showModal();
    else this.sizeChart.setAttribute('open', '');
    document.documentElement.classList.add('sully-sc-lock');
  };

  // Re-read live stock for the given colorways. Resolves with the polos whose size sold out.
  Builder.prototype.refreshStock = function (keys) {
    var self = this;
    var unique = keys.filter(function (k, i) { return k && keys.indexOf(k) === i && self.colorways[k]; });
    return Promise.all(unique.map(function (key) {
      var c = self.colorways[key];
      return fetch(root + 'products/' + encodeURIComponent(c.handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (product) {
          if (!product || !product.variants) return;
          var byId = {};
          product.variants.forEach(function (v) { byId[v.id] = v; });
          Object.keys(c.sizes).forEach(function (s) {
            var live = byId[c.sizes[s].id];
            if (!live) {
              c.sizes[s].available = false;
              return;
            }
            c.sizes[s].available = !!live.available;
            if (!live.available) c.sizes[s].qty = 0;
            if (typeof live.price === 'number') c.sizes[s].price = live.price;
          });
        })
        .catch(function () {});
    })).then(function () {
      var changed = [];
      self.picks.forEach(function (pick, i) {
        if (pick.s && !self.isAvailable(pick.c, pick.s)) {
          pick.s = null;
          changed.push(i);
        }
      });
      self.render();
      return changed;
    });
  };

  Builder.prototype.addToCart = function () {
    if (this.busy || !this.isComplete()) return;
    var self = this;
    var a = this.variant(this.picks[0]);
    var b = this.variant(this.picks[1]);
    // One request, both polos. The same polo twice goes in as one line of 2.
    var items = a.id === b.id
      ? [{ id: a.id, quantity: 2 }]
      : [{ id: a.id, quantity: 1 }, { id: b.id, quantity: 1 }];

    this.busy = true;
    this.clearMessage();
    this.render();

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (body) {
          if (!r.ok || body.status) throw body;
          return body;
        });
      })
      .then(function () { return self.openCart(); })
      .catch(function () {
        // Sold out since the page loaded? Re-read stock, clear that polo's size, open its tab
        // and say so; anything else gets one plain line under the button.
        return self.refreshStock([self.picks[0].c, self.picks[1].c]).then(function (soldOut) {
          if (soldOut.length) {
            self.slot = soldOut[0];
            if (self.picks[self.slot].c) self.shown = self.picks[self.slot].c;
            self.showMessage(self.ds.errorSoldOut);
          } else {
            self.showMessage(self.ds.errorGeneric);
          }
          scrollToEl(self.picker);
        });
      })
      .then(function () {
        self.busy = false;
        self.render();
      });
  };

  Builder.prototype.openCart = function () {
    var drawer = document.querySelector('cart-drawer');
    var cartType = window.theme && window.theme.settings && window.theme.settings.cartType;
    if (!drawer || cartType === 'page') {
      window.location.href = cartUrl;
      return Promise.resolve();
    }
    return new Promise(function (resolve) {
      var opened = false;
      var onOpen = function () {
        opened = true;
        document.removeEventListener('theme:cart-drawer:open', onOpen);
        resolve();
      };
      document.addEventListener('theme:cart-drawer:open', onOpen);

      if (window.GWP_CART_DRAWER_CONFIG || window.BUY_X_GET_Y_CART_DRAWER_CONFIG) {
        // Same path the theme takes when its gift/BOGO cart extension is active
        window.PresidioPlus = window.PresidioPlus || {};
        window.PresidioPlus.gwpProcessed = false;
        document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true, detail: { source: 'theme-add-to-cart' } }));
      } else {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      }

      setTimeout(function () {
        if (opened) return;
        document.removeEventListener('theme:cart-drawer:open', onOpen);
        window.location.href = cartUrl;
        resolve();
      }, 4000);
    });
  };

  /* Phone bar: hidden while the offer card, the picker's own button or the closing button is
     on screen, and while the picker is on screen but not finished (the shopper is already
     choosing). So it shows once the shopper has scrolled past the picker. */
  Builder.prototype.initBar = function () {
    if (!this.bar || !this.barButton) return;
    var self = this;
    document.body.classList.add('sully-bogo-has-bar');

    this.barButton.addEventListener('click', function () {
      if (self.isComplete()) {
        self.addToCart();
        return;
      }
      var m = self.missing();
      if (m !== -1 && m !== self.slot) self.setSlot(m);
      jumpTo(self.picker);
    });

    var watched = all(document, '[data-sully-bogo-cta]');
    var card = this.el.querySelector('.sully-bogo-top__card');
    if (card) watched.push(card);
    if (this.summary) watched.push(this.summary);
    this.barWatched = watched;

    // Positions are read on scroll (once a frame), so the sticky header's live height counts
    var queued = false;
    var queue = function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        self.updateBar();
      });
    };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    this.updateBar();
  };

  Builder.prototype.updateBar = function () {
    if (!this.bar || !this.barWatched) return;
    // Anything under the sticky header counts as off screen
    var top = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    var bottom = window.innerHeight;
    var onScreen = function (node) {
      var r = node.getBoundingClientRect();
      return r.height > 0 && r.bottom > top && r.top < bottom;
    };
    var ctaOnScreen = this.barWatched.some(onScreen);
    var choosing = onScreen(this.picker) && !this.isComplete();
    var hide = ctaOnScreen || choosing;
    this.bar.classList.toggle('is-hidden', hide);
    this.bar.setAttribute('aria-hidden', String(hide));
  };

  /* ------------------------------------------------------------------------
     Video: load when near the screen, play while on screen
     ------------------------------------------------------------------------ */
  function initVideos(scope) {
    var videos = scope.querySelectorAll('video[data-sully-bogo-video]');
    if (!videos.length || reduceMotion) return;
    var load = function (video) {
      if (video.dataset.loaded) return;
      video.dataset.loaded = '1';
      Array.prototype.forEach.call(video.querySelectorAll('source[data-src]'), function (source) {
        source.src = source.getAttribute('data-src');
        source.removeAttribute('data-src');
      });
      video.load();
    };
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(videos, function (v) { load(v); v.play().catch(function () {}); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          load(video);
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () {});
        } else if (video.dataset.loaded) {
          video.pause();
        }
      });
    }, { rootMargin: '200px 0px' });
    Array.prototype.forEach.call(videos, function (v) { observer.observe(v); });
  }

  /* ------------------------------------------------------------------------
     Reviews: Read all / Show fewer
     ------------------------------------------------------------------------ */
  function initReviews(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-sully-bogo-reviews]'), function (section) {
      var toggle = section.querySelector('[data-reviews-toggle]');
      if (!toggle || toggle.dataset.ready) return;
      toggle.dataset.ready = '1';
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        section.classList.toggle('is-expanded', open);
        toggle.setAttribute('aria-expanded', String(open));
        toggle.textContent = open ? toggle.getAttribute('data-label-less') : toggle.getAttribute('data-label-more');
        if (!open && section.getBoundingClientRect().top < 0) jumpTo(section);
      });
    });
  }

  function init(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-sully-bogo-builder]'), function (el) {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      window.SullyBogo.builders.push(new Builder(el));
    });
    initReviews(scope);
    initVideos(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor: sections re-render without running their script tags
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.SullyBogo.formatMoney = formatMoney;
})();
